import { FormConfig, SubmissionData } from '../types';
import { t } from './i18n';

// Get API URL from environment or use default
// SECURITY: Validate that API URL uses HTTPS to prevent MitM attacks
const getApiBaseUrl = (): string => {
  const defaultUrl = 'https://80usneael9.execute-api.eu-west-1.amazonaws.com/dev';
  
  let url: string;
  if (typeof window !== 'undefined') {
    // Browser environment - check for global config or use default
    url = (window as any).HEXAFORMS_API_URL || defaultUrl;
  } else {
    // Node.js environment
    url = process.env.API_BASE_URL || process.env.NEXT_PUBLIC_API_BASE_URL || defaultUrl;
  }
  
  // Validate that URL uses HTTPS
  if (!url.startsWith('https://')) {
    if (typeof window !== 'undefined' && (window as any).HEXAFORMS_DEBUG) {
      console.warn('[HexaForms] API URL debe ser HTTPS. Usando URL por defecto.');
    }
    return defaultUrl;
  }
  
  return url;
};

const API_BASE_URL = getApiBaseUrl();

export async function fetchFormConfig(formId: string, apiKey?: string): Promise<FormConfig> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (apiKey) {
    headers['X-API-Key'] = apiKey;
  }

  const url = `${API_BASE_URL}/public/forms/${formId}/widget-config`;
  
  let response: Response;
  try {
    response = await fetch(url, { headers });
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(
      `No se pudo conectar con el backend. ` +
      `URL: ${url}. ` +
      `Error: ${errorMessage}. ` +
      `Verifica que el backend esté disponible y que la URL sea correcta.`
    );
  }

  if (!response.ok) {
    let errorMessage = `Error ${response.status}: ${response.statusText}`;
    let backendMessage = '';
    
    // Try to get error details from response
    try {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const errorData = await response.json();
        if (errorData.message) {
          backendMessage = errorData.message;
        }
      }
    } catch {
      // Ignore JSON parse errors - backend might return HTML or plain text
    }

    if (response.status === 404) {
      const fullMessage = backendMessage 
        ? `Formulario no encontrado: ${backendMessage}. ` 
        : `Formulario no encontrado. `;
      throw new Error(
        `${fullMessage}` +
        `Form ID: "${formId}". ` +
        `Verifica que el Form ID sea correcto y que el formulario exista en el backend. ` +
        `URL intentada: ${url}`
      );
    }

    const baseMessage = t('error.load');
    const fullMessage = backendMessage 
      ? `${baseMessage}: ${errorMessage}. ${backendMessage}` 
      : `${baseMessage}: ${errorMessage}`;
    
    throw new Error(fullMessage);
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(
      data.message || t('error.backend')
    );
  }

  return data.data;
}

export async function fetchFormBySlug(slug: string): Promise<FormConfig> {
  const response = await fetch(`${API_BASE_URL}/public/forms/slug/${slug}`, {
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to load form: ${response.statusText}`);
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.message || 'Failed to load form');
  }

  return data.data;
}

export async function submitForm(
  formId: string,
  apiKey: string,
  submissionData: SubmissionData
): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/submissions/submit/${formId}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Form-ID': formId,
      'X-API-Key': apiKey,
    },
    body: JSON.stringify(submissionData),
  });

  const data = await response.json();

  if (response.status !== 202) {
    throw new Error(data.message || 'Submission failed');
  }

  return data;
}

export async function trackView(formId: string): Promise<void> {
  try {
    await fetch(`${API_BASE_URL}/public/forms/${formId}/view`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });
  } catch (error) {
    // Silently fail - tracking is not critical
    console.warn('Failed to track view:', error);
  }
}

export async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64 = (reader.result as string).split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
  });
}
